import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { useWizardContext } from '../../contexts/useWizardContext';
import type { CampaignSequenceEntryDto } from '../../api/models/CampaignSequenceEntryDto';
import type { CampaignSequenceEntryWithId } from '../../pages/forms/CampaignSequenceFormDialog';
import { CrudPage } from '../CrudPage';
import { CampaignSequenceEntryDetailDialog } from '../../pages/forms/CampaignSequenceEntryDetailDialog';
import { generateTempUUID } from '../../utils/uuid';
import { CampaignSequenceControllerService } from '../../api/services/CampaignSequenceControllerService';
import { ProductionScheduleControllerService } from '../../api/services/ProductionScheduleControllerService';
import { FeatureControllerService } from '../../api/services/FeatureControllerService';
import { useErrorHandler } from '../../utils/errorHandler';
import { ApiError } from '../../api/core/ApiError';
import { useToast } from '../../hooks/useToast';
import { getEffectiveMaterialId } from '../../utils/aliasProductUtils';

const Step2SequenceSelection: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const { showToast: _showToast } = useToast();
  const {
    planningMode,
    sourceScheduleUuid,
    productionDisplayDemands,
    productionDisplayDemandsAggregated,
    materials,
    isLoadingDemands,
    formData,
    sequenceOptimization,
    setSequenceOptimization,
    selectedDayOfWeek,
    selectedSequenceUuid,
    availableSequencesForDay,
    sequenceEntries,
    autoAddedProductIds,
    setSelectedDayOfWeek,
    setSelectedSequenceUuid,
    setAvailableSequencesForDay,
    setSequenceEntries,
    addSequenceEntry,
    updateSequenceEntry,
    deleteSequenceEntry,
    setAutoAddedProductIds,
    validateSequenceEntries: _validateSequenceEntries
  } = useWizardContext();

  const isReplanning = planningMode === 'REPLANNING';
  const [soFeatureEnabled, setSoFeatureEnabled] = useState(false);

  // Check feature flag for sequence optimization
  useEffect(() => {
    FeatureControllerService.getSequenceOptimization()
      .then((res: any) => setSoFeatureEnabled(res.enabled === true))
      .catch(() => setSoFeatureEnabled(false));
  }, []);

  const [isLoadingSequence, setIsLoadingSequence] = useState(false);
  const lastLoadedDayOfWeekRef = useRef<string | null>(null);
  // Tracks day + demand product IDs from the last auto-add run to allow re-running when new demands arrive
  const lastAutoAddDemandKeyRef = useRef<string | null>(null);
  // Store product IDs from the originally loaded sequence (before auto-add)
  const originalSequenceProductIdsRef = useRef<Set<string>>(new Set());

  const DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

  const getDayOfWeekForPlanningStart = (plannedStart?: string | null): string => {
    if (plannedStart) {
      const datePart = plannedStart.split('T')[0];
      const date = new Date(datePart + 'T12:00:00');
      if (!isNaN(date.getTime())) {
        return DAY_NAMES[date.getDay()];
      }
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return DAY_NAMES[tomorrow.getDay()];
  };

  // Replanning: load the sequence from the source schedule's sequenceSnapshot (section 7.8.7)
  const [isLoadingSourceSequence, setIsLoadingSourceSequence] = useState(false);
  useEffect(() => {
    if (!isReplanning || !sourceScheduleUuid) return;
    let cancelled = false;
    setIsLoadingSourceSequence(true);

    // UUID comparison is case-insensitive: snapshots and API responses return UUIDs partly
    // in UPPERCASE (for example SQL Server CONVERT(NVARCHAR, uniqueidentifier)), while the
    // materials state usually keeps them lowercase. Strict === comparisons would lead to
    // empty productNames + duplicated entries through applyAppended.
    const findMaterialByUuid = (uuid: string | undefined) => {
      if (!uuid) return undefined;
      const needle = uuid.toLowerCase();
      return materials.find((m: any) => typeof m.uuid === 'string' && m.uuid.toLowerCase() === needle);
    };

    const toEntries = (items: Array<{ materialId?: string; sequenceNumber?: number; maxBatches?: number | null }>) =>
      items
        .filter((c) => c.materialId)
        .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
        .map((c, index) => {
          const material = findMaterialByUuid(c.materialId);
          return {
            uuid: generateTempUUID(),
            // Take productId from materials (canonical casing), otherwise pass the original through
            productId: (material?.uuid as string | undefined) ?? c.materialId!,
            productName: material?.name ?? '',
            sequenceNumber: index + 1,
            maxBatches: c.maxBatches ?? undefined
          } as CampaignSequenceEntryDto;
        });

    const applyAppended = (sourceEntries: CampaignSequenceEntryDto[]) => {
      // Lowercase set, so that the comparison against possibly case-different demand IDs is robust.
      const productIdsInSequence = new Set(sourceEntries.map((e) => (e.productId ?? '').toLowerCase()));
      const newTargetByMaterial = new Map<string, number>();
      (productionDisplayDemands ?? []).forEach((d: any) => {
        if (d.materialId) newTargetByMaterial.set(d.materialId, (newTargetByMaterial.get(d.materialId) ?? 0) + (d.quantity ?? 0));
      });
      const newProductIds = Array.from(newTargetByMaterial.entries())
        .filter(([, qty]) => qty > 0)
        .map(([mid]) => getEffectiveMaterialId(mid, materials))
        .filter((id): id is string => !!id)
        .filter((id) => !productIdsInSequence.has(id.toLowerCase()));
      const dedupedNew = Array.from(new Set(newProductIds));
      const maxSeq = sourceEntries.length;
      const appended: CampaignSequenceEntryDto[] = dedupedNew.map((productId, i) => {
        const material = findMaterialByUuid(productId);
        return {
          uuid: generateTempUUID(),
          productId: (material?.uuid as string | undefined) ?? productId,
          productName: material?.name ?? '',
          sequenceNumber: maxSeq + i + 1,
          maxBatches: undefined
        } as CampaignSequenceEntryDto;
      });
      setSequenceEntries([...sourceEntries, ...appended]);
      setAutoAddedProductIds(new Set(appended.map((e) => e.uuid!).filter(Boolean)));
    };

    (async () => {
      try {
        const schedule = await ProductionScheduleControllerService.findByUuid(sourceScheduleUuid);
        if (cancelled || !schedule) return;
        const snapshot = schedule.sequenceSnapshot;
        if (!snapshot?.trim()) {
          handleError(new Error('Source plan has no sequenceSnapshot'), 'loading source plan sequence');
          return;
        }
        let sourceEntries: CampaignSequenceEntryDto[];
        try {
          const parsed = JSON.parse(snapshot) as { campaigns?: Array<{ campaignId?: string; materialId?: string; sequenceNumber?: number }> };
          sourceEntries = toEntries(parsed?.campaigns ?? []);
        } catch (err) {
          handleError(err instanceof Error ? err : new Error(String(err)), 'parsing source plan sequenceSnapshot');
          return;
        }
        if (!cancelled) applyAppended(sourceEntries);
      } catch (err) {
        if (!cancelled) handleError(err, 'loading source plan sequence');
      } finally {
        if (!cancelled) setIsLoadingSourceSequence(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isReplanning, sourceScheduleUuid, materials, productionDisplayDemands, setSequenceEntries, setAutoAddedProductIds, handleError]);

  // Derive selectedDayOfWeek from plannedStart: initial value, and sync whenever plannedStart changes.
  // A manual dropdown change (without plannedStart changing) is preserved.
  const lastSyncedPlannedStartRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (isReplanning) return;
    const currentPlannedStart = formData.plannedStart ?? null;
    const isInitial = lastSyncedPlannedStartRef.current === undefined;
    const plannedStartChanged = !isInitial && lastSyncedPlannedStartRef.current !== currentPlannedStart;
    if (isInitial || plannedStartChanged) {
      lastSyncedPlannedStartRef.current = currentPlannedStart;
      const derived = getDayOfWeekForPlanningStart(currentPlannedStart);
      if (derived !== selectedDayOfWeek) {
        setSelectedDayOfWeek(derived);
      }
    }
  }, [isReplanning, formData.plannedStart, selectedDayOfWeek, setSelectedDayOfWeek]);

  // Load list of sequences for day and default sequence when day of week changes (skip for replanning)
  useEffect(() => {
    if (isReplanning || !selectedDayOfWeek) {
      return;
    }

    // Reset auto-added tracking when day of week changes
    if (lastLoadedDayOfWeekRef.current !== selectedDayOfWeek) {
      lastAutoAddDemandKeyRef.current = null;
    }

    // Only load if day of week changed
    if (lastLoadedDayOfWeekRef.current === selectedDayOfWeek) {
      return;
    }

    // Set ref BEFORE async call to prevent duplicate loadSequence() starts
    // when the effect re-fires while the first call is still in-flight.
    lastLoadedDayOfWeekRef.current = selectedDayOfWeek;

    let cancelled = false;

    const loadSequence = async () => {
      try {
        setIsLoadingSequence(true);
        const list = await CampaignSequenceControllerService.getCampaignSequencesByDayOfWeek(selectedDayOfWeek);
        if (cancelled) return;
        setAvailableSequencesForDay(list.map(s => ({ uuid: s.uuid, name: s.name, isDefault: s.isDefault })));

        if (list.length === 0) {
          setSelectedSequenceUuid(null);
          setSequenceEntries([]);
          originalSequenceProductIdsRef.current = new Set();
          setAutoAddedProductIds(new Set());
          lastAutoAddDemandKeyRef.current = null;
        } else {
          const defaultSequence = await CampaignSequenceControllerService.getCampaignSequenceByDayOfWeek(selectedDayOfWeek);
          if (cancelled) return;
          if (defaultSequence?.uuid) {
            setSelectedSequenceUuid(defaultSequence.uuid);
            if (defaultSequence.entries && defaultSequence.entries.length > 0) {
              setSequenceEntries(defaultSequence.entries);
              const originalProductIds = new Set(
                defaultSequence.entries
                  .filter((entry: any) => entry.productId)
                  .map((entry: any) => entry.productId!)
              );
              originalSequenceProductIdsRef.current = originalProductIds;
            } else {
              setSequenceEntries([]);
              originalSequenceProductIdsRef.current = new Set();
            }
            setAutoAddedProductIds(new Set());
            lastAutoAddDemandKeyRef.current = null;
          } else {
            setSelectedSequenceUuid(null);
            setSequenceEntries([]);
            originalSequenceProductIdsRef.current = new Set();
            setAutoAddedProductIds(new Set());
            lastAutoAddDemandKeyRef.current = null;
          }
        }
      } catch (error: any) {
        if (cancelled) return;
        const isNotFound = error instanceof ApiError && error.status === 404;
        if (!isNotFound) {
          console.error('❌ Error loading sequence:', error);
          handleError(error, 'loading campaign sequence');
        }
        setSelectedSequenceUuid(null);
        setSequenceEntries([]);
        originalSequenceProductIdsRef.current = new Set();
        setAutoAddedProductIds(new Set());
        lastAutoAddDemandKeyRef.current = null;
      } finally {
        if (!cancelled) {
          setIsLoadingSequence(false);
        }
      }
    };

    loadSequence();

    return () => { cancelled = true; };
  }, [isReplanning, selectedDayOfWeek, setSequenceEntries, setAutoAddedProductIds, setSelectedSequenceUuid, setAvailableSequencesForDay, handleError]);

  // Auto-add products from demands that are not in sequence (skip for replanning - handled above)
  useEffect(() => {
    if (isReplanning || !selectedDayOfWeek || productionDisplayDemandsAggregated.length === 0 || isLoadingSequence || isLoadingDemands) {
      return;
    }

    if (lastLoadedDayOfWeekRef.current !== selectedDayOfWeek) {
      return;
    }

    // Get unique effective product IDs from demands. Uses productionDisplayDemandsAggregated so that
    // long-term managed products (whose demand date lies beyond the planning horizon but whose
    // production date — demandDate - longTermOffsetDays — falls inside it) are included.
    // Filter to [plannedStart, plannedEnd]: the feed covers 7 days from plannedStart; without the
    // filter, single-day schedules would automatically pick up products from the following days.
    const ps = formData.plannedStart?.split('T')[0];
    const pe = formData.plannedEnd?.split('T')[0];
    const productIdsFromDemands = new Set<string>();
    productionDisplayDemandsAggregated.forEach((demand: any) => {
      if (!demand.materialId) return;
      if ((demand.quantity ?? 0) <= 0) return;
      const dateKey = demand.demandDate?.split('T')[0] || '';
      if (ps && pe && dateKey && (dateKey < ps || dateKey > pe)) return;
      const material = materials.find((m: any) => m.uuid === demand.materialId);
      if (!material || material.materialType !== 'PRODUCT') return;
      const effectiveId = getEffectiveMaterialId(demand.materialId, materials);
      if (effectiveId) productIdsFromDemands.add(effectiveId);
    });

    // Skip if demand product set hasn't changed since last auto-add for this day
    const demandKey = `${selectedDayOfWeek}|${Array.from(productIdsFromDemands).sort().join(',')}`;
    if (lastAutoAddDemandKeyRef.current === demandKey) {
      return;
    }

    // Get product IDs already in sequence (use current state)
    const currentEntries = sequenceEntries;
    const productIdsInSequence = new Set<string>(
      currentEntries
        .filter((entry: any) => entry.productId)
        .map((entry: any) => entry.productId as string)
    );

    // Find missing products
    const missingProductIds = Array.from(productIdsFromDemands).filter(
      (productId: string) => !productIdsInSequence.has(productId)
    );

    // Always update tracking key so we don't re-run with the same demand set
    lastAutoAddDemandKeyRef.current = demandKey;

    if (missingProductIds.length === 0) {
      return;
    }

    // Get product names for missing products (main product name from materials)
    const productMap = new Map<string, string>();
    missingProductIds.forEach((productId: string) => {
      const material = materials.find((m: any) => m.uuid === productId);
      if (material?.name) productMap.set(productId, material.name);
    });

    // Add missing products to sequence (use current state)
    const maxSequenceNumber = currentEntries.length > 0
      ? Math.max(...currentEntries.map((e: any) => e.sequenceNumber || 0))
      : 0;

    const newEntries: CampaignSequenceEntryDto[] = missingProductIds.map((productId: string, index: number) => {
      const entry: CampaignSequenceEntryDto = {
        uuid: generateTempUUID(),
        productId: productId as string,
        productName: productMap.get(productId) || '',
        sequenceNumber: maxSequenceNumber + index + 1,
        maxBatches: undefined
      };
      return entry;
    });

    // Add to sequence and mark as auto-added
    setSequenceEntries((prev: any) => {
      const updated = [...prev, ...newEntries];
      const newAutoAddedIds = new Set<string>();
      const originalProductIds = originalSequenceProductIdsRef.current;
      
      updated.forEach((entry: any) => {
        if (entry.uuid && productIdsFromDemands.has((entry.productId as string) || '')) {
          const wasInOriginalSequence = originalProductIds.has((entry.productId as string) || '');
          if (!wasInOriginalSequence) {
            newAutoAddedIds.add(entry.uuid);
          }
        }
      });
      autoAddedProductIds.forEach((id: string) => newAutoAddedIds.add(id));
      setAutoAddedProductIds(newAutoAddedIds);
      return updated;
    });
  }, [isReplanning, selectedDayOfWeek, productionDisplayDemandsAggregated, materials, sequenceEntries, autoAddedProductIds, isLoadingSequence, isLoadingDemands, setSequenceEntries, setAutoAddedProductIds]);

  const weekdayOptions = [
    { value: 'MONDAY', text: t('weekday.MONDAY') },
    { value: 'TUESDAY', text: t('weekday.TUESDAY') },
    { value: 'WEDNESDAY', text: t('weekday.WEDNESDAY') },
    { value: 'THURSDAY', text: t('weekday.THURSDAY') },
    { value: 'FRIDAY', text: t('weekday.FRIDAY') },
    { value: 'SATURDAY', text: t('weekday.SATURDAY') },
    { value: 'SUNDAY', text: t('weekday.SUNDAY') }
  ];

  const formatMaxBatches = (data: CampaignSequenceEntryDto) => {
    return data.maxBatches !== null && data.maxBatches !== undefined 
      ? String(data.maxBatches) 
      : '-';
  };

  const formatProductName = useCallback((data: CampaignSequenceEntryDto) => {
    // Check if this entry is auto-added by checking its UUID against autoAddedProductIds
    const entryUuid = data.uuid;
    const isAutoAdded = entryUuid ? autoAddedProductIds.has(entryUuid) : false;
    
    return (
      <span style={{ color: isAutoAdded ? 'red' : 'inherit' }}>
        {data.productName || ''}
      </span>
    );
  }, [autoAddedProductIds]);

  const entriesGridColumns = [
    { 
      field: 'productName', 
      headerKey: 'campaignSequenceEntry.grid.field.materialName',
      width: 250,
      render: formatProductName
    },
    { 
      field: 'maxBatches', 
      headerKey: 'campaignSequenceEntry.grid.field.maxBatches', 
      width: 150,
      render: formatMaxBatches
    }
  ];

  const handleEntriesAdd = useCallback((_selectedItems: CampaignSequenceEntryWithId[]) => {
    const currentEntries = sequenceEntries || [];
    const newSequenceNumber = currentEntries.length + 1;
    
    const newEntry: CampaignSequenceEntryDto = {
      uuid: generateTempUUID(),
      productId: '',
      productName: '',
      sequenceNumber: newSequenceNumber,
      maxBatches: undefined
    };
    
    const entryWithId: CampaignSequenceEntryWithId = { ...newEntry, id: newEntry.uuid || '' };
    return entryWithId;
  }, [sequenceEntries]);

  const handleEntriesPriorityChange = useCallback(async (reorderedEntries: any[]) => {
    const entriesWithoutId = reorderedEntries.map((entry: any, index: number) => {
      const { id, ...entryData } = entry;
      entryData.sequenceNumber = index + 1;
      return entryData;
    });
    
    setSequenceEntries(entriesWithoutId);
  }, [setSequenceEntries]);

  const handleEntriesDelete = useCallback((selectedItems: any[]) => {
    if (selectedItems.length === 0) return;
    
    const uuidsToDelete = selectedItems.map((item: any) => item.uuid || item.id);
    
    // Filter out auto-added entries
    const deletableUuids = uuidsToDelete.filter((uuid: string) => !autoAddedProductIds.has(uuid));
    
    if (deletableUuids.length === 0) {
      // All selected items are auto-added, show message or do nothing
      return;
    }
    
    const filteredEntries = sequenceEntries.filter((entry: any) => !deletableUuids.includes(entry.uuid || ''));
    
    // Re-sequence numbers
    const updatedEntries = filteredEntries.map((entry: any, index: number) => ({
      ...entry,
      sequenceNumber: index + 1
    }));
    
    setSequenceEntries(updatedEntries);
  }, [sequenceEntries, autoAddedProductIds, setSequenceEntries]);

  // Fetch data function that always uses current sequenceEntries
  const fetchEntriesData = useCallback((): Promise<CampaignSequenceEntryWithId[]> => {
    const sortedEntries = [...sequenceEntries].sort((a: any, b: any) => {
      const aNum = a.sequenceNumber || 0;
      const bNum = b.sequenceNumber || 0;
      return aNum - bNum;
    });
    const result = sortedEntries.map((entry: any) => ({
      ...entry,
      id: entry.uuid || generateTempUUID()
    }));
    return Promise.resolve(result);
  }, [sequenceEntries]);

  const sequenceDropdownSource = useMemo(() => availableSequencesForDay.map((s: { uuid?: string; name?: string; isDefault?: boolean }) => ({
    uuid: s.uuid,
    name: s.name,
    text: (s.name || s.uuid || '') + (s.isDefault ? ` (${t('campaignSequence.defaultLabel', 'Standard')})` : '')
  })), [availableSequencesForDay, t]);

  const handleSequenceChange = useCallback(async (e: any) => {
    const uuid = e?.value as string | undefined;
    if (!uuid) return;
    try {
      setIsLoadingSequence(true);
      const sequence = await CampaignSequenceControllerService.getCampaignSequenceByUuid(uuid);
      setSelectedSequenceUuid(sequence.uuid || null);
      setSequenceEntries(sequence.entries || []);
      const newOriginalProductIds = new Set(
        (sequence.entries || [])
          .filter((entry: any) => entry.productId)
          .map((entry: any) => entry.productId!)
      );
      originalSequenceProductIdsRef.current = newOriginalProductIds;
      setAutoAddedProductIds(new Set());
      lastAutoAddDemandKeyRef.current = null;
    } catch (err) {
      handleError(err, 'loading sequence');
    } finally {
      setIsLoadingSequence(false);
    }
  }, [setSelectedSequenceUuid, setSequenceEntries, setAutoAddedProductIds, handleError]);

  return (
    <div className="space-y-6">
      <div>
        {!isReplanning && (
          <>
            {/* Sequence mode selection — only shown when feature flag is enabled */}
            {soFeatureEnabled && (
              <div className="mb-4 flex gap-6">
                <RadioButtonComponent
                  label={t('campaignSequence.wizard.manualSequence', 'Vordefinierte Sequenz')}
                  name="sequenceMode"
                  value="manual"
                  checked={!sequenceOptimization}
                  change={() => setSequenceOptimization(false)}
                  cssClass="e-small pb-radio-primary"
                />
                <RadioButtonComponent
                  label={t('campaignSequence.wizard.autoSequence', 'Automatische Reihenfolge')}
                  name="sequenceMode"
                  value="auto"
                  checked={sequenceOptimization}
                  change={() => setSequenceOptimization(true)}
                  cssClass="e-small pb-radio-primary"
                />
              </div>
            )}

            {/* Manual sequence: weekday + sequence dropdown */}
            {!sequenceOptimization && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('campaignSequence.form.weekday')}*
                  </label>
                  <DropDownListComponent
                    dataSource={weekdayOptions}
                    fields={{ text: 'text', value: 'value' }}
                    value={selectedDayOfWeek || ''}
                    change={(e: any) => setSelectedDayOfWeek((e.value as string) || undefined)}
                    placeholder={t('campaignSequence.form.weekdayPlaceholder')}
                    floatLabelType="Never"
                    cssClass="w-full"
                  />
                </div>

                {sequenceDropdownSource.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('campaignSequence.wizard.sequenceSelect', 'Sequenz')}
                    </label>
                    <DropDownListComponent
                      dataSource={sequenceDropdownSource}
                      fields={{ text: 'text', value: 'uuid' }}
                      value={selectedSequenceUuid || ''}
                      change={handleSequenceChange}
                      placeholder={t('campaignSequence.wizard.sequencePlaceholder', 'Sequenz wählen')}
                      floatLabelType="Never"
                      cssClass="w-full"
                      enabled={!isLoadingSequence}
                    />
                  </div>
                )}
              </>
            )}

            {/* Automatic sequence: read-only product list */}
            {sequenceOptimization && (
              <div className="mb-4">
                <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                  {t('campaignSequence.wizard.autoSequenceInfo', 'Die Reihenfolge wird beim Einplanen automatisch optimiert. Alle Produkte mit Bedarf werden eingeplant.')}
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('campaignSequence.wizard.productsWithDemand', 'Produkte mit Bedarf')}
                </label>
                <div className="border border-gray-200 rounded max-h-[600px] overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left border-b">{t('material.articleNumber', 'Artikelnr.')}</th>
                        <th className="px-3 py-2 text-left border-b">{t('material.name', 'Produkt')}</th>
                        <th className="px-3 py-2 text-right border-b">{t('demand.quantity', 'Menge')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productionDisplayDemandsAggregated
                        .filter((d: any) => {
                          const mat = materials.find((m: any) => m.uuid === d.materialId);
                          if (!mat || mat.materialType !== 'PRODUCT' || (d.quantity ?? 0) <= 0) return false;
                          // productionDisplayDemandsAggregated covers 7 days from plannedStart; in the sequence grid
                          // we only want the planning horizon, otherwise several days add up to a weekly
                          // quantity (above all for single-day schedules).
                          const ps = formData.plannedStart?.split('T')[0];
                          const pe = formData.plannedEnd?.split('T')[0];
                          const dateKey = d.demandDate?.split('T')[0] || '';
                          if (!ps || !pe || !dateKey) return true;
                          return dateKey >= ps && dateKey <= pe;
                        })
                        .reduce((acc: any[], d: any) => {
                          const effectiveId = getEffectiveMaterialId(d.materialId, materials);
                          const existing = acc.find((a: any) => a.effectiveId === effectiveId);
                          if (existing) {
                            existing.quantity += d.quantity ?? 0;
                          } else {
                            const mat = materials.find((m: any) => m.uuid === effectiveId);
                            acc.push({ effectiveId, name: mat?.name || '', articleNumber: mat?.articleNumber || '', quantity: d.quantity ?? 0 });
                          }
                          return acc;
                        }, [])
                        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
                        .map((item: any, idx: number) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-1.5 border-b text-gray-500">{item.articleNumber}</td>
                            <td className="px-3 py-1.5 border-b">{item.name}</td>
                            <td className="px-3 py-1.5 border-b text-right">{item.quantity}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {isReplanning && isLoadingSourceSequence && (
          <p className="text-gray-600 mb-4">{t('common.loading', 'Loading...')}</p>
        )}

        {!sequenceOptimization && <div className="mt-6">
          <CrudPage<CampaignSequenceEntryWithId>
            titleKey="campaignSequence.form.entries"
            showTitle={false}
            gridHeight="400px"
            localMode={true}
            showDeleteButton={false}
            gridMeta={entriesGridColumns}
            refreshKey={`sequence-${selectedDayOfWeek}-${sequenceEntries.length}-${autoAddedProductIds.size}`}
            fetchData={fetchEntriesData}
            saveData={async (entry: CampaignSequenceEntryWithId) => {
              // Create a clean copy of the entry data to avoid cyclic references
              const { id, ...entryData } = entry;
              const cleanEntryData: CampaignSequenceEntryDto = {
                uuid: entryData.uuid,
                productId: entryData.productId,
                productName: entryData.productName,
                sequenceNumber: entryData.sequenceNumber,
                maxBatches: entryData.maxBatches
              };
              
              const existingEntry = sequenceEntries.find((e: any) => e.uuid === cleanEntryData.uuid);
              
              if (existingEntry) {
                updateSequenceEntry(cleanEntryData.uuid || '', cleanEntryData);
              } else {
                addSequenceEntry(cleanEntryData);
              }
              
              // Re-sequence numbers
              const currentEntries = existingEntry
                ? sequenceEntries.map((e: any) => e.uuid === cleanEntryData.uuid ? cleanEntryData : e)
                : [...sequenceEntries, cleanEntryData];
              
              const resequencedEntries = currentEntries.map((e: any, index: number) => ({
                ...e,
                sequenceNumber: index + 1
              }));
              
              setSequenceEntries(resequencedEntries);
              
              // Return the saved entry for CrudPage to handle
              return {
                ...cleanEntryData,
                id: cleanEntryData.uuid || generateTempUUID()
              } as CampaignSequenceEntryWithId;
            }}
            deleteData={async (id: string) => {
              // Don't allow deletion of auto-added entries
              if (autoAddedProductIds.has(id)) {
                // Cannot delete auto-added entries
                return;
              }
              
              deleteSequenceEntry(id as string);
            }}
            FormComponent={({ value, onClose, onSave }: any) => {
              const entryWithUuid = value ? {
                ...value,
                uuid: value.id || value.uuid
              } : value;
              
              // Check if product occurs multiple times
              const productId = entryWithUuid?.productId as string | undefined;
              const productOccurrences = productId 
                ? sequenceEntries.filter((e: any) => e.productId === productId).length
                : 0;
              const canSetMaxBatches = productOccurrences > 1;
              
              return (
                <CampaignSequenceEntryDetailDialog
                  value={entryWithUuid}
                  onClose={onClose}
                  onSave={(entryData: any) => {
                    const entryWithId = { ...entryData, id: entryData.uuid };
                    onSave(entryWithId);
                  }}
                  allEntries={sequenceEntries}
                  canSetMaxBatches={canSetMaxBatches}
                />
              );
            }}
            showDefaultToolbar={true}
            showAddButton={!isReplanning}
            showEditButton={!isReplanning}
            showCloneButton={false}
            showSearchButton={false}
            allowFiltering={false}
            allowSorting={false}
            noPadding={true}
            showSummary={false}
            hasPriority={!isReplanning}
            priorityField="sequenceNumber"
            onPriorityChange={isReplanning ? undefined : async (entries: any[]) => {
              await handleEntriesPriorityChange(entries);
            }}
            onAdd={handleEntriesAdd}
            onDelete={handleEntriesDelete}
          />
        </div>}
      </div>
    </div>
  );
};

export default Step2SequenceSelection;
